import type { ProductFeatureGroup } from "@itaas/pep-contracts";
import type { PepLocale } from "../../i18n/types";

export interface ProductAudienceItem {
  id: string;
  icon: string;
  titleFr: string;
  titleEn: string;
  bodyFr: string;
  bodyEn: string;
}

export interface ProductOutcomeItem {
  id: string;
  icon: string;
  titleFr: string;
  titleEn: string;
  bodyFr: string;
  bodyEn: string;
}

export interface ProductTrustChip {
  id: string;
  labelFr: string;
  labelEn: string;
}

export interface LocalizedPair {
  fr: string;
  en: string;
}

/** Shared HD Product Detail configuration (data-driven per product). */
export interface ProductDetailHdConfig {
  productImageSrc: string;
  productImageAlt: LocalizedPair;
  humanImageSrc: string;
  humanImageAlt: LocalizedPair;
  /** Optional figcaption override (defaults to product-agnostic copy). */
  visualCaption?: LocalizedPair;
  /** Used when catalog has no heroKicker. */
  heroKicker?: LocalizedPair;
  audiences: ProductAudienceItem[];
  audienceTitle: LocalizedPair;
  outcomes: ProductOutcomeItem[];
  outcomesLead: LocalizedPair;
  /** Used when catalog has no featureGroups. */
  capabilityGroups?: ProductFeatureGroup[];
  contextTitle: LocalizedPair;
  contextLead: LocalizedPair;
  /** When catalog has no highlights — short factual bullets only. */
  contextFallbackPoints?: LocalizedPair[];
  /** Trust chips without trial/promo (added dynamically). */
  trustBaseChips: ProductTrustChip[];
  showAmbassadorLink?: boolean;
  /** Optional ambassador CTA label (when showAmbassadorLink). */
  ambassadorLabel?: LocalizedPair;
  /** Hide edition showcase when product has no edition matrix (e.g. RH). Default true. */
  showEditionShowcase?: boolean;
  /**
   * CTA strategy for products without self-service trial/configure.
   * - configure (default): trial/configure primary, demo secondary
   * - demo: demo primary, contact secondary
   * - contact: contact primary (addon / no demo-trial path)
   */
  ctaPriority?: "configure" | "demo" | "contact";
  /**
   * Prefer editorial capabilityGroups over catalog featureGroups when set.
   * Default: use catalog featureGroups if present, else config.
   */
  preferConfigCapabilities?: boolean;
}

export function localizeAudience(item: ProductAudienceItem, locale: PepLocale) {
  return {
    title: locale === "en" ? item.titleEn : item.titleFr,
    body: locale === "en" ? item.bodyEn : item.bodyFr,
  };
}

export function localizeOutcome(item: ProductOutcomeItem, locale: PepLocale) {
  return {
    title: locale === "en" ? item.titleEn : item.titleFr,
    body: locale === "en" ? item.bodyEn : item.bodyFr,
  };
}

export function pickLocale(pair: LocalizedPair, locale: PepLocale): string {
  return locale === "en" ? pair.en : pair.fr;
}

/** Trust chips — only items grounded in SoT / product truth. */
export function buildProductTrustChips(
  base: ProductTrustChip[],
  input: {
    trialDays?: number;
    promoActive: boolean;
    promoBadgeFr?: string;
    promoBadgeEn?: string;
  },
): ProductTrustChip[] {
  const chips = [...base];
  if (input.trialDays && input.trialDays > 0) {
    chips.unshift({
      id: "trial",
      labelFr: "Création d'environnement en ligne",
      labelEn: "Online environment setup",
    });
  }
  if (input.promoActive && (input.promoBadgeFr || input.promoBadgeEn)) {
    chips.push({
      id: "promo",
      labelFr: input.promoBadgeFr
        ? `Offre de lancement ${input.promoBadgeFr}`
        : "Offre de lancement",
      labelEn: input.promoBadgeEn
        ? `Launch offer ${input.promoBadgeEn}`
        : "Launch offer",
    });
  }
  return chips;
}
